import React, { useMemo, useState } from "react";
import cn from "classnames";
import Modal from "../../../components/Modal";
import { CHANNELS } from "../shared";
import ContentPicker from "./ContentPicker";
import { TEMPLATES, applyTemplate } from "./templates";
import styles from "./TemplatePicker.module.sass";

/**
 * Selector de PLANTILLAS: la lista de la izquierda, los huecos a rellenar a la
 * derecha. Al aceptar devuelve el parche de estado ya interpolado, para que el
 * editor solo tenga que aplicarlo.
 */
const TemplatePicker = ({ open, onClose, onApply }) => {
  const [selectedId, setSelectedId] = useState(TEMPLATES[0]?.id);
  const [values, setValues] = useState({});
  const [content, setContent] = useState(null);

  const template = useMemo(() => TEMPLATES.find((t) => t.id === selectedId), [selectedId]);

  const select = (t) => {
    setSelectedId(t.id);
    setValues({});
    setContent(null);
  };

  /**
   * Elegir el deck/historia rellena de golpe nombre, nivel, nº de ítems, emoji,
   * id y portada: no hay que copiar un ObjectId a mano desde ninguna parte.
   */
  const pickContent = (picked) => {
    setContent(picked);
    setValues((prev) => ({
      ...prev,
      nombre: picked.nombre,
      n: picked.n,
      nivel: picked.nivel,
      emoji: picked.emoji || prev.emoji || "",
      id: picked.id,
      imagen: picked.imageUrl || "",
    }));
  };

  // Los huecos vacíos se rellenan con el placeholder: una plantilla siempre debe
  // salir "entera", nunca con un {{nombre}} colgando en mitad del texto.
  const resolved = useMemo(() => {
    if (!template) return {};
    const out = {};
    (template.fields || []).forEach((f) => {
      out[f.key] = (values[f.key] ?? "").trim() || f.placeholder || "";
    });
    return out;
  }, [template, values]);

  const missing = (template?.fields || []).filter(
    (f) => f.required && !(values[f.key] ?? "").trim()
  );

  const apply = () => {
    if (!template) return;
    onApply(applyTemplate(template, resolved), template);
    onClose();
  };

  return (
    <Modal visible={open} onClose={onClose} outerClassName={styles.modal}>
      <div className={styles.head}>
        <div className={styles.title}>Plantillas</div>
        <div className={styles.sub}>
          Anuncios que se mandan una y otra vez, ya escritos en los cinco idiomas.
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.list}>
          {TEMPLATES.map((t) => (
            <button
              key={t.id}
              type="button"
              className={cn(styles.item, { [styles.itemActive]: t.id === selectedId })}
              onClick={() => select(t)}
            >
              <span className={styles.itemEmoji}>{t.emoji}</span>
              <span className={styles.itemBody}>
                <span className={styles.itemName}>{t.label}</span>
                <span className={styles.itemChannels}>
                  {t.channels.map((c) => CHANNELS[c]?.label || c).join(" + ")}
                </span>
              </span>
            </button>
          ))}
        </div>

        {template && (
          <div className={styles.detail}>
            <div className={styles.detailTitle}>
              {template.emoji} {template.label}
            </div>
            <div className={styles.detailText}>{template.description}</div>

            {template.contentType && (
              <div className={styles.picker}>
                <span className={styles.fieldLabel}>
                  {template.contentType === "text" ? "¿Qué historia?" : "¿Qué deck?"}
                </span>
                <ContentPicker
                  type={template.contentType}
                  value={content}
                  onSelect={pickContent}
                />
                <span className={styles.fieldHint}>
                  Al elegirlo se rellenan nombre, nivel, nº de ítems, portada y el deeplink.
                </span>
              </div>
            )}

            <div className={styles.fields}>
              {(template.fields || []).map((f) => (
                <label key={f.key} className={styles.field}>
                  <span className={styles.fieldLabel}>
                    {f.label}
                    {f.required && <span className={styles.req}>*</span>}
                  </span>
                  <input
                    className={styles.input}
                    placeholder={f.placeholder}
                    value={values[f.key] ?? ""}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  />
                  {f.hint && <span className={styles.fieldHint}>{f.hint}</span>}
                </label>
              ))}
              {!template.fields?.length && (
                <div className={styles.detailText}>Esta plantilla no necesita datos.</div>
              )}
            </div>

            <div className={styles.previewBox}>
              <div className={styles.previewLabel}>Así queda el título (ES)</div>
              <div className={styles.previewTitle}>
                {applyTemplate(template, resolved).titles.es || "—"}
              </div>
              {/* Las portadas del catálogo vienen FIRMADAS y caducan (B2, 7 días):
                  vale para un anuncio de esta semana, no para un mail. */}
              {content?.imageUrl?.includes("Expires=") && (
                <div className={styles.previewLink}>
                  ⚠️ La portada del catálogo caduca en unos días. Para algo que vaya a estar
                  semanas, sube una imagen propia en el paso 2.
                </div>
              )}
              {template.linkUrl && (
                <div className={styles.previewLink}>
                  Destino: <code>{applyTemplate(template, resolved).linkUrl}</code>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className={styles.actions}>
        {missing.length > 0 && (
          <span className={styles.warn}>
            Sin rellenar: {missing.map((f) => f.label).join(", ")} — se usará el ejemplo.
          </span>
        )}
        <button className="button-stroke" onClick={onClose}>
          Cancelar
        </button>
        <button className="button" onClick={apply}>
          Usar plantilla
        </button>
      </div>
    </Modal>
  );
};

export default TemplatePicker;
